import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { ServerProgressBarSpinnerComponent } from 'src/app/controls/server-progress-bar-spinner/server-progress-bar-spinner.component';
import { ViewCloningData } from 'src/app/controls/view-cloning-dialog/models/view-cloning-data';
import { ViewCloningDialogComponent } from 'src/app/controls/view-cloning-dialog/view-cloning-dialog.component';
import { HttpError } from 'src/app/core/http-error';
import { EmailAddress } from 'src/app/models/email-address';
import { Rights } from 'src/app/models/enums/rights';
import { IViewAccessibility } from 'src/app/models/interfaces/view-accessibility.interface';
import { IView } from 'src/app/models/interfaces/view.interface';
import { View } from 'src/app/models/view';
import { EmailAddressesService } from 'src/app/services/emailAddress.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { ViewsService } from 'src/app/services/views.service';
import { FormMode } from '../../controls/common/form-mode';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { ViewDetailContext } from './view-detail-context';
import { ViewDetailComponent } from './view-detail/view-detail.component';
import { ViewFormContext } from './view-form-context';
import { ViewsListComponent } from './views-list/views-list.component';

enum DisplayMode {
    List = 1,
    Form = 2,
}

@Component({
    selector: 'app-views',
    templateUrl: './views.component.html',
    styleUrls: ['./views.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, ViewsListComponent, DetailHeaderComponent, ViewDetailComponent, ServerProgressBarSpinnerComponent],
})
export class ViewsComponent implements OnInit {
    /**
     * Reference au composant ViewsList.
     */
    @ViewChild(ViewsListComponent) viewsList: ViewsListComponent;

    @ViewChild(ServerProgressBarSpinnerComponent) serverProgressBarSpinner: ServerProgressBarSpinnerComponent;

    /**
     * Liste des enums utilisés dans le template
     */
    public FormMode = FormMode;
    public DisplayMode = DisplayMode;

    /**
     * Contexte du formulaire.
     */
    public context: ViewDetailContext = null;

    /**
     * Mode d'affichage de la liste (Liste, Arborescence, formulaire)
     */
    public displayMode = DisplayMode.List;

    /**
     * Indique si le bouton d'ajout/Edition doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Liste des adresses de l'organisation courante pouvant emettre.
     */
    addresses: EmailAddress[];

    /**
     * Constructeur.
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param viewsService : Service métier utilisé pour gérer les vues.
     * @param memoryService : Service utilisé pour gérer le contexte courant.
     */
    constructor(
        protected notificationService: NotificationService,
        public dialog: MatDialog,
        private emailAddressesService: EmailAddressesService,
        private viewsService: ViewsService,
        private organizationsService: OrganizationsService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {}

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ViewCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ViewEdit);

        this.loadAddresses();
    }

    /**
     * Appellé lorsque l'utilisateur selectionne une vue.
     * @param view Vue selectionnée.
     */
    public onViewSelected(view: View) {
        this.createContext();
        this.loadView(view.id).subscribe((x) => {
            this.refresh(View.createFormCopy(x), FormMode.Reading);
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param view Vue sélectionnée.
     */
    public onViewDisplayed(view: View) {
        this.createContext();
        this.loadView(view.id).subscribe((x) => {
            this.refresh(View.createFormCopy(x), FormMode.Reading);
        });
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param view Vue sélectionnée.
     */
    public onViewEdited(view: View) {
        this.createContext();
        // Cas de la mise à jour.
        this.loadView(view.id).subscribe((x) => {
            this.refresh(View.createFormCopy(x), FormMode.Edition);
        });
    }

    /**
     * Appelé lorsque les données de la vue ont été mise à jour.
     * Il s'agit des modifications apportées à l'onglet Infos.
     * @param view : Valeur de la vue retourné par le formulaire.
     */
    public onViewUpdated(view: View): void {
        this.spinner.show('main-spinner');
        if (this.context.editedViewId) {
            // Cas de la mise à jour.
            this.viewsService.update(this.context.editedViewId, view).subscribe({
                next: (x) => {
                    this.viewsList.refresh();

                    this.refreshWithNotification(View.createFormCopy(x), 'Vue mise à jour', FormMode.Reading);
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour la vue.' + error.message);
                    this.spinner.hide('main-spinner');
                },
            });
        } else {
            // Cas de la création.
            this.viewsService.add(view).subscribe({
                next: (x) => {
                    this.viewsList.refresh();
                    this.refreshWithNotification(View.createFormCopy(x), 'Vue créée', FormMode.Reading);
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer la vue.' + error.message);
                    this.spinner.hide('main-spinner');
                },
            });
        }
    }

    /**
     * Appelé lorsque les accès aux vues ont été mis à jour.
     * Il s'agit des modifications apportées à l'onglet Accessibilité.
     * @param viewsAccessibility : Accès aux vues à mettre à jour.
     */
    public onAccessibilitiesUpdated(accessibilities: IViewAccessibility[]): void {
        this.viewsService.updateAccessibilities(this.context.editedViewId, accessibilities).subscribe((x) => {
            this.refreshWithNotification(this.context.viewContext.view, 'Accessibilité de la vue mise à jour', FormMode.Reading);
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onViewCreated() {
        this.displayForm(View.createFormCopy(new View()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param view Vue sélectionnée.
     */
    public onViewDuplicated(view: View) {
        let duplicatedView = View.createFormCopy(view);
        duplicatedView.id = null;
        this.displayForm(duplicatedView, FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param view Vue sélectionnée.
     */
    public onViewDeleted(view: View) {
        this.serverProgressBarSpinner.openProgressBarPanel();
        // Suppression effective de la vue.
        this.viewsService.delete(view.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Vue supprimée');
                this.viewsList.refresh();
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'La suppression de la vue a échouée.');
                this.serverProgressBarSpinner.closeProgressBarPanel();
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Activer l'archivage' dans le menu contextuel.
     * @param view Vue sélectionnée.
     */
    public onViewArchivingActivated(view: View) {
        this.spinner.show('main-spinner');
        // Activation de l'archivage de la vue.
        this.viewsService.activateArchiving(view.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, "L'archivage de la vue a été activé");
                this.viewsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError("L'activation de l'archivage de la vue a échouée.", error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Cloner' dans le menu contextuel.
     * @param view Vue sélectionnée.
     */
    public onViewCloned(view: View) {
        this.organizationsService.organisationsAllowedToAccessHub4BChange$.subscribe((x) => {
            const dialogRef = this.dialog.open(ViewCloningDialogComponent, {
                width: '600px',
                data: {
                    organizations: x,
                    defaultOrganization: this.memoryService.getSelectedOrganization(),
                    originalView: view,
                } as ViewCloningData,
            });

            // Attente de la réponse de la boite de dialogue de saisie.
            dialogRef.afterClosed().subscribe((dialogResult: ViewCloningData) => {
                if (dialogResult.organizationId) {
                    this.serverProgressBarSpinner.openProgressBarPanel();
                    // Activation de l'archivage de la vue.
                    this.viewsService.clone(view, dialogResult).subscribe(
                        (x) => {
                            this.notificationService.displaySuccess(null, 'La vue a été clonée.');
                            this.viewsList.refresh();
                        },
                        (error: HttpError) => {
                            this.notificationService.displayError('Le clonage de la vue a échouée.', error.message);
                            this.serverProgressBarSpinner.closeProgressBarPanel();
                        }
                    );
                }
            });
        });
    }

    /**
     * Appllé lors de l'export.
     */
    public onViewsExported() {
        this.createContext();
        this.viewsList.export('Vues.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onViewsImported() {
        this.viewsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onViewsHelped() {
        this.viewsList.displayHelpDialog('views');
    }

    /**
     * Charge la liste des adresses email de l'organization pouvant emettre.
     */
    private loadAddresses() {
        this.spinner.show('main-spinner');
        this.emailAddressesService.List(null, null, null, null, 0, 1000).subscribe(
            (x) => {
                this.addresses = x.value.filter((x) => x.sendingEnabled === true);
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de charger la liste des adresses mail pouvant emettre.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Retourne une valeur indiquant si le formulaire est affiché (visible)
     * @returns True si le formulaire est visible.
     */
    public isFormDisplayed(): boolean {
        return DisplayMode.Form === (this.displayMode & DisplayMode.Form);
    }

    /**
     * Cache le formulaire.
     * La liste ou l'arboresence est alors visible.
     */
    public hideForm(): void {
        this.displayMode &= ~DisplayMode.Form;
    }

    /**
     * Définit le mode d'affichage de la page (Form / List).
     * @param mode Mode d'affichage
     */
    public setDisplayMode(mode: DisplayMode) {
        if (mode == DisplayMode.Form) {
            this.displayMode |= mode;
        } else {
            this.displayMode = mode;
        }
    }

    /**
     * Charge les informations d'une vue.
     * Il s'agit des données affichées dans l'onglet Infos du détail.
     * @param viewId : Id de la vue à charger.
     * @returns Les informations de la vue.
     */
    public loadView(viewId: number): Observable<View> {
        return this.viewsService.get(viewId);
    }

    /**
     * Affiche Les données d'une vue dans le formulaire.
     * @param view : Instance de la vue à afficher dans le formulaire.
     * @param formMode Mode d'affichage du formulaire.
     */
    displayForm(view: IView, formMode: FormMode): void {
        let context = this.fillForm(view);
        context.viewContext.formVisible = true;
        context.formMode = formMode;

        this.context = context;

        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param user : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    public fillForm(view: IView): ViewDetailContext {
        let context = this.createContext();
        context.viewContext.view = view;
        context.editedViewId = view.id;
        return context;
    }

    /**
     * Rafraichi la page.
     * @param view : Valeur du client retourné par le formulaire.
     * @param mode : Mode d'affichage.
     */
    public refresh(view: IView, mode: FormMode): void {
        this.viewsList.refresh();
        this.displayForm(view, mode);
        this.fillForm(view);
    }

    /**
     * Rafraichi la page.
     * @param view : Valeur du client retourné par le formulaire.
     * @param notificationString: Texte de la notification.
     * @param mode : Mode d'affichage.
     */
    public refreshWithNotification(view: IView, notificationString: string, mode: FormMode): void {
        this.refresh(view, mode);
        this.notificationService.displaySuccess(null, notificationString);
        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Créer un nouveau contexte.
     */
    private createContext(): ViewDetailContext {
        let context = new ViewDetailContext();
        (context.viewContext as ViewFormContext).addresses = this.addresses;
        return context;
    }
}
